import { technischerFehlerText } from "./technische-fehler";
import { ApiError } from "../data";
import { translateDbMessage } from "../pages/creator/fehler";
import { tarifModulMeldung } from "../model/tarif";
import { currentLang, type Lang } from "./sprache";

/**
 * Meldungen der Datenbank in der Agentur-Oberfläche (PARITAET-8). Die Datenbank meldet auf Deutsch und setzt bei den
 * häufigen Fällen einen Hinweis-Schlüssel (hint, z. B. „perm:confirm_payments“, „org:read_only“). Für Englisch übersetzt
 * diese Zuordnung über den Schlüssel; ohne Schlüssel greifen bekannte Sätze, sonst bleibt die Meldung wörtlich
 * (lieber deutsch als leer). Muster wie im Creator-Portal (pages/creator/fehler.ts).
 */

/** Hinweis-Schlüssel der Datenbank → englische Meldung. Ein Schlüssel mit „:“ am Ende gilt als Präfix. */
const HINT_EN: Record<string, string> = {
  // fehlende Rechte (app.require_permission)
  "perm:confirm_payments": "Missing permission: confirm payments.",
  "perm:sign_contracts": "Missing permission: sign contracts.",
  "perm:approve_invoices": "Missing permission: approve invoices.",
  "perm:send_invoices": "Missing permission: send invoices.",
  "perm:dun": "Missing permission: send reminders.",
  "perm:trigger_payouts": "Missing permission: make payouts.",
  "perm:": "Missing permission for this action. Contact your admin.",
  // Agentur und Tarif
  "org:read_only": "This agency currently uses CreatorOffice read-only (trial expired, blocked or cancelled). Changes are not possible.",
  "limit:module_insights": "The Insights module is not included in the plan.",
  "limit:max_contracts_per_month": "Plan limit reached: no more contracts can be sent this month.",
  "limit:storage_gb": "The plan's storage is full. Remove files you no longer need or choose a larger plan. Invoices, receipts and contracts can still be stored.",
  "limit:trials": "Trials are already running. Please finish one of them or choose a subscription.",
  "limit:": "Plan limit reached. The admin can choose a larger plan.",
  "setup:company": "Company details incomplete: legal entity, representation, choice of law and place of jurisdiction are needed before the first dispatch.",
  "setup:invoice_number_prefix": "The prefix for invoice numbers is missing (Settings → Terms).",
  "member:owner": "Only the owner themselves or platform operations can change the agency owner's role and access.",
  // 0092: zweiter Faktor auch für Rechte und Rollen, Konto löschen als einziger Admin, Angebote mit fremden Creatorn
  "auth:mfa_required": "This step needs confirmation with your second factor. Sign out, sign in again and enter the code from your authenticator app (Account · Security).",
  "auth:mfa_setup_required": "Your agency requires a second factor for this step and you have not set one up yet. Set it up under Account · Security (authenticator app) and try again.",
  "account:sole_chef": "You are the only admin of an agency. Transfer the role to another person or cancel the agency before you have your account deleted.",
  "account:sole_chef_readonly": "You are the only admin of a read-only agency and remain responsible for its retained records until it is deleted. Request early deletion of the agency under Settings · Plan, or ask platform operations to appoint another admin. Then you can have your account deleted.",
  "offer:hidden_lines": "This offer contains creators you do not manage. Sending, new versions and cancellations are handled by someone who sees all creators of the offer.",
  "talent:agency_field": "Your agency manages this field.",
  "invite:already_member": "You are already a member of this agency. The admin changes the role under Team & permissions.",
  "invite:guardian_email": "The email address of the parent or guardian is invalid.",
  // Rechnungen, Mahnungen, Auszahlungen (app.period_protection, invoice_*)
  "block:PERIOD_HAS_INVOICES": "There are already invoices for this month. Please have them cancelled or withdrawn first.",
  "block:PERIOD_CLOSED": "This month is closed (cancelled or already paid).",
  // 0497 (Prüf-Chat Nachtest 03.11.2026): Monatsrechnung für einen ohne Honorar stornierten Monat
  "block:PERIOD_CANCELLED_NO_FEE": "This service month was cancelled without a fee. An invoice for this month cannot be issued; if there is a cancellation fee, close the month with a cancellation fee.",
  "block:DUNNING_ORDER": "This dunning level skips the order. Please choose the next level or state a reason.",
  "block:DUNNING_DEADLINE_RUNNING": "The deadline of the last reminder is still running.",
  "block:NO_INVOICE_FOR_MODEL": "There is no invoice for this remuneration model.",
  "block:TALENT_REPORTED_PAID": "The creator has already reported the payment. Check the report first.",
  "block:brand_unpaid": "The brand has not paid yet. The setting is “Payout only after the brand's payment”.",
  "block:PARTIAL_PAYOUT_OPEN": "Only part has been paid out so far. The rest follows with the next payout run; only then is the invoice paid.",
  "use:invoice_record_payment": "Record the payment of an invoice to the creator with amount and date (Record incoming payment).",
  "block:": "This step is not possible in the current state.",
  "confirm:brand_unpaid": "The brand has not paid yet. Please explain why the payout is made in advance.",
  "use:invoice_confirm_payment": "Creator invoices are booked via “Confirm payment” (creator's report and proof).",
  "use:invoice_cancel": "Agency invoices are issued. Please cancel them and create a new one.",
  "missing:payout_details": "Payout details are missing (creator profile · payout).",
  // 0444 (Steuerprüfung 23.10.2026): Datum der Gutschriftvereinbarung, Aufbewahrung verlängert
  "setup:self_billing_agreement_date": "The self-billing agreement is dated after the self-billing invoice. A self-billing invoice requires an agreement made beforehand (section 14(2) sentence 5 German VAT Act). Please check the invoice date or the date of the agreement (creator profile · invoicing).",
  "org:retention_hold": "Retention has been extended for this agency (hold set by platform operations). Final deletion is paused.",
  "retention_hold:reason": "Please state the reason (e.g. tax audit, objection, open assessment), at most 500 characters.",
  "retention_hold:none": "Retention has not been extended for this agency.",
  "import:dunning": "The dunning details of this row cannot be taken over.",
  "import:amount": "The amounts of this row do not add up (net, tax, gross).",
  "import:number": "The invoice number is missing or too long.",
  "import:issue_date": "The invoice date is missing or in the future.",
  "import:currency": "The currency is not supported or does not match the deal.",
  "import:tax_mode": "The tax treatment is missing, unknown or does not match the tax amount.",
  "import:paid": "The paid amount or payment date is not plausible.",
  "import:": "This row cannot be taken over.",
  // Kooperationen, Beiträge, Anfragen
  "deal:cancelled": "The deal is cancelled.",
  "deal:archived": "The deal is archived.",
  "deliverable:closed": "The post is already live or has been dropped.",
  "deliverable:period": "The month does not belong to this deal or post.",
  "content:pending": "A submission is already waiting for review. Approve it or request a correction first.",
  "content:files": "Only files the creator sent in the chat can be selected.",
  // 0155 Content-Ablage
  "content:media": "Only fully uploaded files of this post that are not yet in a version can be selected.",
  "media:type": "This file type is not supported. Allowed: MP4, MOV, WebM, JPG, PNG, HEIC, WebP and PDF.",
  "media:size": "The file is too large (videos up to 2 GB, images and PDF up to 100 MB) or arrived incompletely.",
  "media:name": "The file name is invalid.",
  "media:busy": "Several uploads are already running. Please finish or cancel them first.",
  "media:not_uploading": "This upload is already complete or was cancelled. Please choose the file again.",
  "media:parts": "Parts of the file are missing. Please resume the upload.",
  "media:type_mismatch": "The content does not match the file extension. Please upload the original file.",
  "media:network": "No connection. The upload can be resumed.",
  "media:not_configured": "Uploading is not possible right now. Please send your draft as a link or in the chat.",
  // K-583: ohne erkennbaren Tarif im Satz (videoGrenzeEn) neutral, nicht fest „Solo“ und „Team“
  "limit:media_video_max": "Your plan's videos to get started are used up. Choose a larger plan for more videos.",
  "limit:media_video_mb": "This video is larger than your plan allows. Larger videos are available in a larger plan.",
  "comment:body": "A comment needs 1 to 2,000 characters.",
  "comment:pos": "The point in the image is invalid.",
  "comment:time": "The timestamp is outside the video.",
  "comment:many": "This file already has the maximum number of comments.",
  "link:file": "File not found.",
  "catalog:platform": "Unknown platform. The admin maintains platforms under Settings · Terms.",
  "inquiry:closed": "The inquiry is closed. Reopen it to make changes.",
  "candidate:reason": "Please choose a reason from the list.",
  "offer:locked": "An issued offer stays unchanged. Create a new version.",
  "review:email": "Please enter a valid email address for the brand.",
  "review:closed": "This approval has already been completed.",
  "consent:missing": "The creator has not consented to sharing with brands.",
  "share:revoked": "This share has been withdrawn.",
  "share:expired": "This share has expired.",
  "share:locked": "Too many failed attempts. The share is locked.",
  "share:unavailable": "This share is currently not available.",
  // Verträge
  "check:signer": "A signatory (name and email) is missing.",
  // Prüfung Vertragsbaukasten 0–2 (0114–0116)
  "check:charref": "The contract text contains a character reference that screen and PDF would show differently. Please write an “&” in the text as “&amp;”.",
  "check:print": "The contract text contains a character the PDF cannot print (such as an emoji). Please replace it.",
  "check:text_after_signature": "There is still text after the last signature field, or more than a short label between two signature fields. Please place it before the signatures.",
  "check:signature_in_private": "There is a signature field in the confidential part. Signing takes place at the end of the main part.",
  "check:private_part_agency": "The agency always sees the confidential part; it cannot be excluded from it.",
  "check:signer_email_unique": "Two signatories have the same email address. Each person who signs needs their own address.",
  "signer:agency": "The agency's signatories are added on sending from the registered representation; in the draft only enter brand and creator.",
  "fill:chars": "The value contains invisible or control characters (e.g. for text direction). Please type it again.",
  // Prüfung Vertragsbaukasten Runde 2 (0118, 0119)
  "fill:print": "The value contains a character the PDF cannot print (such as an emoji). Please replace it.",
  "sign:name_chars": "The name contains characters that are not allowed or that the PDF cannot print. Please type the name again.",
  "check:signer_name": "A signatory's name contains characters that are not allowed or that the PDF cannot print. Please type the name again.",
  "check:title": "The contract title contains characters that are not allowed or that the PDF cannot print.",
  "template:status": "A new template starts as a draft. Only an active version is archived; a draft that is no longer needed is deleted.",
  "perm:edit_contract_text": "The contract text does not match the template, master data and details of the draft, or the template was never activated. Please save the draft again; only admins and team members with the permission “Change contract text” can send a deviating text.",
  "template:blocks_mismatch": "Blocks and template text do not match. Please save the template again in the builder.",
  "check:unresolved": "The contract text still contains open placeholders.",
  "check:signature_block": "A signature block is missing in the contract text.",
  "contract:closed": "This contract can no longer be signed.",
  "contract:void": "The contract has been rescinded.",
  "signer:signed": "This party has already signed.",
  "locked:signed": "The contract has already been signed. Details can no longer be changed.",
  "countersign:same_person": "The second agency signature must be given by another person.",
  "version:parties": "A new version stays with the same creator and brand. For other parties create the contract as a copy.",
  "document:kind": "Only a contract of the type “Brand's document” gets a PDF for signing.",
  "talent:not_signer": "The creator does not sign this contract.",
  "fill:": "Please fill in the required field first.",
  "wait_fill:": "The contract is not complete yet: a field still has to be filled in.",
  "link:invalid": "This link is invalid.",
  "link:expired": "This link has expired.",
  "link:used": "This link has already been used.",
  "link:declined": "The contract was declined via this link.",
  "link:withdrawn": "This link is no longer valid.",
  "link:read_expired": "This read access has expired.",
  "link:use_view": "This link is for signing.",
};

/** Bekannte Meldungen ohne Hinweis-Schlüssel, die das Team trifft. Ohne Punkt am Ende gilt ein Präfix. */
const MESSAGE_EN: Record<string, string> = {
  "Recht fehlt: ": "Missing permission for this action.",
  "Diese Agentur nutzt CreatorOffice derzeit nur lesend": "This agency currently uses CreatorOffice read-only. Changes are not possible.",
  "Tarifgrenze erreicht": "Plan limit reached. The admin can choose a larger plan.",
  "Nicht angemeldet.": "Not signed in.",
  "Keine Berechtigung.": "Not permitted.",
};

/**
 * K-583: Video-Grenzen der Content-Ablage (0409) nennen Tarif und Ziel aus dem Katalog („Im Tarif Creator sind zum Kennenlernen
 * 10 Videos enthalten. Für mehr Videos wähle den Tarif Creator Pro.“). Englisch mit denselben Namen; „Agentur“ heißt „Agency“.
 */
export function videoGrenzeEn(message: string): string | null {
  const en = (name: string) => name.replace(/^Agentur\b/, "Agency");
  const m = message.trim();
  const anzahl = /^Im Tarif (.+?) sind zum Kennenlernen (\d+) Videos enthalten\.(?: Für mehr Videos wähle den Tarif (.+?)\.)?/.exec(m);
  if (anzahl) {
    return `The ${en(anzahl[1]!)} plan includes ${anzahl[2]} videos to get started. ` + (anzahl[3] ? `Choose the ${en(anzahl[3])} plan for more videos.` : "Choose a larger plan for more videos.");
  }
  const groesse = /^Videos dürfen im Tarif (.+?) höchstens (\d+) MB groß sein\.(?: Größere Videos gibt es ab dem Tarif (.+?)\.)?/.exec(m);
  if (groesse) {
    return `Videos in the ${en(groesse[1]!)} plan may be at most ${groesse[2]} MB. ` + (groesse[3] ? `Larger videos are available from the ${en(groesse[3])} plan.` : "Larger videos are available in a larger plan.");
  }
  return null;
}

function hintText(hint: string | undefined): string | null {
  if (!hint) return null;
  const exact = HINT_EN[hint];
  if (exact) return exact;
  const prefix = hint.includes(":") ? hint.slice(0, hint.indexOf(":") + 1) : null;
  return prefix ? HINT_EN[prefix] ?? null : null;
}

/**
 * Meldung eines Fehlers in der Oberflächensprache (Agentur). Deutsch bleibt die Meldung unverändert, außer der Tarifsperre
 * „tarif_modul:<schlüssel>“ (0099): Sie wird in beiden Sprachen zum Satz „Diese Funktion ist im Tarif Team enthalten.“,
 * mit dem Tarif aus dem Hinweis der Datenbank, sonst aus der festen Zuordnung.
 */
export function fehlerText(error: unknown, lang: Lang = currentLang()): string {
  const message = error instanceof Error ? error.message : String(error);
  const tarif = tarifModulMeldung(message, error instanceof ApiError ? error.hint : undefined, lang);
  if (tarif) return tarif;
  const technisch = technischerFehlerText(message, error instanceof ApiError ? error.code : undefined, lang);
  if (technisch) return technisch;
  if (lang !== "en") return message;
  const video = videoGrenzeEn(message);
  if (video) return video;
  if (error instanceof ApiError) {
    const byHint = hintText(error.hint);
    if (byHint) return byHint;
  }
  const m = message.trim();
  const exact = MESSAGE_EN[m];
  if (exact) return exact;
  for (const [de, en] of Object.entries(MESSAGE_EN)) if (!/[.!?]$/.test(de) && m.startsWith(de)) return en;
  return translateDbMessage(m, lang);
}
